import React from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { Empty, Input, Select, message } from 'antd';
import type { InputRef, SelectProps } from 'antd';
import './index.less';

export type KeywordPickerOption = {
  label: React.ReactNode;
  value: string;
  volume?: number;
};

type BaseKeywordPickerSelectProps = {
  className?: string;
  options: KeywordPickerOption[];
  loading?: boolean;
  hasMore?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  autoFocus?: boolean;
  /** 关键词 word 上限（按空格切分），非字符数 */
  maxKeywordLength?: number;
  onSearch: (value: string) => void;
  onOpen?: () => void;
  onLoadMore: () => void;
  onBlur?: () => void;
  /** 表单场景默认 true，点外部关闭；表格单元格编辑传 false，由外层 mousedown 结束编辑 */
  closeOnOutsideClick?: boolean;
};

type SingleKeywordPickerSelectProps = BaseKeywordPickerSelectProps & {
  mode?: 'single';
  value?: string;
  allowCustom?: boolean;
  onChange: (value: string) => void;
};

type MultipleKeywordPickerSelectProps = BaseKeywordPickerSelectProps & {
  mode: 'multiple';
  value?: string[];
  maxTagCount?: number;
  onChange: (value: string[]) => void;
};

export type KeywordPickerSelectProps = SingleKeywordPickerSelectProps | MultipleKeywordPickerSelectProps;

export type KeywordPickerFlushResult = {
  value: string | string[];
  rejected?: boolean;
};

export type KeywordPickerSelectHandle = {
  /** 把未回车的输入按回车规则写入 value；表格点外面时先调这个再提交 */
  flushPendingInput: () => KeywordPickerFlushResult;
};

const LOAD_MORE_THRESHOLD = 100;
const POPUP_CLASS_NAME = 'keyword-picker-select__popup';

function normalizeKeyword(keyword: string) {
  return keyword.trim();
}

function countWords(text: string) {
  const normalized = String(text || '').trim();
  if (!normalized) {
    return 0;
  }
  return normalized.split(/\s+/).filter(Boolean).length;
}

function dedupeKeywords(values: string[]) {
  const result: string[] = [];
  const seen = new Set<string>();
  values.forEach((item) => {
    if (seen.has(item)) {
      return;
    }
    seen.add(item);
    result.push(item);
  });
  return result;
}

const KeywordPickerSelect = React.forwardRef<KeywordPickerSelectHandle, KeywordPickerSelectProps>((props, ref) => {
  const { className, options, loading = false, hasMore = false, placeholder, searchPlaceholder = '搜索关键词', autoFocus = false, maxKeywordLength, closeOnOutsideClick = true, onSearch, onOpen, onLoadMore } = props;
  const [searchValue, setSearchValue] = React.useState('');
  const [selectorInput, setSelectorInput] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const popupInteractRef = React.useRef(false);
  const searchInputRef = React.useRef<InputRef>(null);
  const blurTimerRef = React.useRef<number>();
  const propsRef = React.useRef(props);
  const selectorInputRef = React.useRef('');
  propsRef.current = props;
  selectorInputRef.current = selectorInput;

  // maxKeywordLength 按 word 计数（空格分隔），不是字符数
  const isKeywordAllowed = (keyword: string) => !maxKeywordLength || countWords(keyword) <= maxKeywordLength;

  const flushPendingInput = React.useCallback((rawInput?: string): KeywordPickerFlushResult => {
    const currentProps = propsRef.current;
    const maxLen = currentProps.maxKeywordLength;
    const allowed = (keyword: string) => !maxLen || countWords(keyword) <= maxLen;
    const inputValue = normalizeKeyword(rawInput ?? selectorInputRef.current);
    selectorInputRef.current = '';
    setSelectorInput('');

    if (currentProps.mode === 'multiple') {
      const currentValues = currentProps.value || [];
      if (!inputValue) {
        return { value: currentValues };
      }
      if (!allowed(inputValue)) {
        message.warning(`次关键词最多 ${maxLen} 个词`);
        return { value: currentValues, rejected: true };
      }
      if (currentValues.includes(inputValue)) {
        message.warning('该关键词已存在');
        return { value: currentValues, rejected: true };
      }
      const maxTagCount = currentProps.maxTagCount;
      if (typeof maxTagCount === 'number' && currentValues.length >= maxTagCount) {
        message.warning(`最多 ${maxTagCount} 个次关键词`);
        return { value: currentValues, rejected: true };
      }
      const nextValues = [...currentValues, inputValue];
      currentProps.onChange(nextValues);
      return { value: nextValues };
    }

    const currentValue = currentProps.value || '';
    if (!inputValue) {
      return { value: currentValue };
    }
    if (!allowed(inputValue)) {
      message.warning(`关键词最多 ${maxLen} 个词`);
      return { value: currentValue, rejected: true };
    }
    if (currentProps.allowCustom) {
      currentProps.onChange(inputValue);
      return { value: inputValue };
    }
    return { value: currentValue };
  }, []);

  React.useImperativeHandle(ref, () => ({ flushPendingInput }), [flushPendingInput]);

  const isFocusInsidePopup = () => {
    const activeElement = document.activeElement;
    if (!activeElement) {
      return false;
    }
    return Boolean(activeElement.closest(`.${POPUP_CLASS_NAME}`));
  };

  const triggerSearch = (value: string) => {
    setSearchValue(value);
    // 纯空格不当作有效搜索，交给上层按空关键字恢复缓存、不发请求
    onSearch(value.trim() ? value.trim() : '');
  };

  const openDropdown = () => {
    // 打开时只清本地搜索框，并从父级缓存恢复列表，不重复请求接口
    setSearchValue('');
    onOpen?.();
    setOpen(true);
  };

  const keepPopupOpen = () => {
    popupInteractRef.current = true;
    setOpen(true);
  };

  const handleDropdownVisibleChange = (nextOpen: boolean) => {
    if (nextOpen) {
      openDropdown();
      return;
    }
    window.setTimeout(() => {
      if (closeOnOutsideClick) {
        if (isFocusInsidePopup()) {
          setOpen(true);
          return;
        }
        popupInteractRef.current = false;
        setOpen(false);
        setSelectorInput('');
        return;
      }
      // 弹层内交互时不要关；真正点外面由单元格外层 mousedown 结束编辑并卸载
      if (popupInteractRef.current || isFocusInsidePopup()) {
        setOpen(true);
        return;
      }
      setOpen(false);
    }, 0);
  };

  const handleSelectBlur = () => {
    if (blurTimerRef.current) {
      window.clearTimeout(blurTimerRef.current);
    }
    blurTimerRef.current = window.setTimeout(() => {
      if (closeOnOutsideClick) {
        if (isFocusInsidePopup()) {
          return;
        }
        popupInteractRef.current = false;
        setOpen(false);
        setSelectorInput('');
        return;
      }
      if (popupInteractRef.current || isFocusInsidePopup()) {
        setOpen(true);
        return;
      }
      // 仅收起弹层，不向上抛 onBlur 结束单元格编辑（由外层点击判断）
      setOpen(false);
      setSelectorInput('');
    }, 150);
  };

  React.useEffect(
    () => () => {
      if (blurTimerRef.current) {
        window.clearTimeout(blurTimerRef.current);
      }
    },
    [],
  );

  const handlePopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - LOAD_MORE_THRESHOLD && hasMore && !loading) {
      onLoadMore();
    }
  };

  const focusPopupSearch = () => {
    keepPopupOpen();
    window.setTimeout(() => {
      searchInputRef.current?.focus({ preventScroll: true });
    }, 0);
  };

  const renderOptionList = (selectedValues: string[], onPick: (value: string) => void) => (
    <div className="keyword-picker-select__list" onScroll={handlePopupScroll}>
      {options.length ? (
        <>
          {options.map((item) => {
            const selected = selectedValues.includes(item.value);
            return (
              <div
                key={item.value}
                className={`keyword-picker-select__option${selected ? ' keyword-picker-select__option--selected' : ''}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  keepPopupOpen();
                }}
                onClick={() => onPick(item.value)}
              >
                <span className="keyword-picker-select__option-label">{item.label}</span>
                {item.volume !== undefined && item.volume !== null ? <span className="keyword-picker-select__option-volume">{Number(item.volume).toLocaleString()}</span> : null}
                {selected ? <CheckOutlined className="keyword-picker-select__option-check" /> : null}
              </div>
            );
          })}
          {(loading || !hasMore) && <div className="keyword-picker-select__footer">{loading ? '加载中...' : '没有更多数据了'}</div>}
        </>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={loading ? '加载中...' : '暂无数据'} className="keyword-picker-select__empty" />
      )}
    </div>
  );

  const popupRender: SelectProps['popupRender'] = () => (
    <div className={POPUP_CLASS_NAME} onMouseDown={keepPopupOpen}>
      <div
        className="keyword-picker-select__search"
        onKeyDown={(event) => event.stopPropagation()}
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          focusPopupSearch();
        }}
      >
        <Input.Search
          ref={searchInputRef}
          allowClear
          size="small"
          value={searchValue}
          placeholder={searchPlaceholder}
          loading={loading}
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            focusPopupSearch();
          }}
          onKeyDown={(event) => event.stopPropagation()}
          onFocus={() => {
            popupInteractRef.current = true;
            setOpen(true);
          }}
          onBlur={() => {
            // 焦点仍可能回到 Select / 弹层其它区域，短暂保留交互标记
            window.setTimeout(() => {
              if (!isFocusInsidePopup()) {
                popupInteractRef.current = false;
              }
            }, 0);
          }}
          onChange={(event) => setSearchValue(event.target.value)}
          onSearch={triggerSearch}
          onClear={() => triggerSearch('')}
        />
      </div>
      {props.mode === 'multiple'
        ? renderOptionList(props.value || [], (keyword) => {
            const normalizedKeyword = normalizeKeyword(keyword);
            if (!normalizedKeyword || !isKeywordAllowed(normalizedKeyword)) {
              if (normalizedKeyword && !isKeywordAllowed(normalizedKeyword)) {
                message.warning(`次关键词最多 ${maxKeywordLength} 个词`);
              }
              return;
            }
            const currentValues = props.value || [];
            if (currentValues.includes(normalizedKeyword)) {
              props.onChange(currentValues.filter((item) => item !== normalizedKeyword));
              return;
            }
            if (props.maxTagCount && currentValues.length >= props.maxTagCount) {
              message.warning(`最多 ${props.maxTagCount} 个次关键词`);
              return;
            }
            props.onChange([...currentValues, normalizedKeyword]);
            setSelectorInput('');
          })
        : renderOptionList(props.value ? [props.value] : [], (keyword) => {
            const normalizedKeyword = normalizeKeyword(keyword);
            if (!normalizedKeyword || !isKeywordAllowed(normalizedKeyword)) {
              if (normalizedKeyword && !isKeywordAllowed(normalizedKeyword)) {
                message.warning(`关键词最多 ${maxKeywordLength} 个词`);
              }
              return;
            }
            props.onChange(normalizedKeyword);
            setSelectorInput('');
          })}
    </div>
  );

  const sharedSelectProps: Pick<SelectProps, 'open' | 'autoFocus' | 'className' | 'options' | 'showSearch' | 'searchValue' | 'filterOption' | 'placeholder' | 'popupRender' | 'autoClearSearchValue' | 'onSearch' | 'onFocus' | 'onDropdownVisibleChange' | 'onBlur'> = {
    open,
    autoFocus,
    className,
    options: [],
    showSearch: true,
    searchValue: selectorInput,
    filterOption: false,
    placeholder,
    popupRender,
    autoClearSearchValue: true,
    onSearch: setSelectorInput,
    onFocus: openDropdown,
    onDropdownVisibleChange: handleDropdownVisibleChange,
    onBlur: handleSelectBlur,
  };

  if (props.mode === 'multiple') {
    const maxTagCount = props.maxTagCount;
    const currentValues = props.value || [];

    const handleMultipleChange = (nextValue: Array<string | number>) => {
      const normalizedValues = dedupeKeywords(
        nextValue
          .map((item) => normalizeKeyword(String(item)))
          .filter(Boolean)
          .filter(isKeywordAllowed),
      );
      props.onChange(typeof maxTagCount === 'number' ? normalizedValues.slice(0, maxTagCount) : normalizedValues);
      setSelectorInput('');
    };

    return (
      <Select
        {...sharedSelectProps}
        mode="tags"
        allowClear
        value={currentValues}
        tokenSeparators={[',', '\n']}
        maxCount={maxTagCount}
        onChange={handleMultipleChange}
        onInputKeyDown={(event) => {
          if (event.key !== 'Enter') {
            return;
          }
          const inputValue = normalizeKeyword(selectorInput || (event.target as HTMLInputElement).value || '');
          if (!inputValue) {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          flushPendingInput(inputValue);
        }}
      />
    );
  }

  if (props.allowCustom) {
    return (
      <Select
        {...sharedSelectProps}
        mode="tags"
        allowClear
        value={props.value ? [props.value] : []}
        tokenSeparators={[',', '\n']}
        onChange={(nextValue) => {
          // 主关键词只保留最新一个；去掉 maxCount，避免已有值后无法再输入覆盖
          const currentValue = normalizeKeyword(String(nextValue[nextValue.length - 1] || ''));
          if (!currentValue || !isKeywordAllowed(currentValue)) {
            if (currentValue && !isKeywordAllowed(currentValue)) {
              message.warning(`关键词最多 ${maxKeywordLength} 个词`);
            }
            props.onChange('');
            setSelectorInput('');
            return;
          }
          props.onChange(currentValue);
          setSelectorInput('');
        }}
        onInputKeyDown={(event) => {
          if (event.key !== 'Enter') {
            return;
          }
          const inputValue = normalizeKeyword(selectorInput || (event.target as HTMLInputElement).value || '');
          if (!inputValue) {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          flushPendingInput(inputValue);
        }}
      />
    );
  }

  return <Select {...sharedSelectProps} allowClear value={props.value || undefined} onChange={(nextValue) => props.onChange(nextValue || '')} />;
});

KeywordPickerSelect.displayName = 'KeywordPickerSelect';

export default KeywordPickerSelect;
